import {GroupBillingAgent} from 'ApplicationEntry/models/GroupBillingAgent';
import {NewYorkBillingAgent} from 'ApplicationEntry/models/NewYorkBillingAgent';
import {OrdinaryBillingAgent} from 'ApplicationEntry/models/OrdinaryBillingAgent';

export type BillingAgent = GroupBillingAgent | NewYorkBillingAgent | OrdinaryBillingAgent;

type ExhaustiveBillingAgentOptionsByType<TReturn> = {
  Group: (group: GroupBillingAgent) => TReturn;
  NewYork: (newYork: NewYorkBillingAgent) => TReturn;
  Ordinary: (ordinary: OrdinaryBillingAgent) => TReturn;
};

type PartialBillingAgentOptionsByType<TReturn> = {
  Group?: (group: GroupBillingAgent) => TReturn;
  NewYork?: (newYork: NewYorkBillingAgent) => TReturn;
  Ordinary?: (ordinary: OrdinaryBillingAgent) => TReturn;
  _: (app: BillingAgent) => TReturn;
};

const isExhaustiveByType = <TReturn>(
  options: BillingAgentByTypeOptions<TReturn>
): options is ExhaustiveBillingAgentOptionsByType<TReturn> =>
  !!options.Group && !!options.NewYork && !!options.Ordinary;

type BillingAgentByTypeOptions<TReturn> =
  | PartialBillingAgentOptionsByType<TReturn>
  | ExhaustiveBillingAgentOptionsByType<TReturn>;

export const matchBillingAgentByType = <TReturn>(options: BillingAgentByTypeOptions<TReturn>) => (
  app: BillingAgent
): TReturn => {
  switch (app.key) {
    case 'InvalidGroupBillingAgent':
    case 'ValidGroupBillingAgent':
      return isExhaustiveByType(options) ? options.Group(app) : options.Group ? options.Group(app) : options._(app);
    case 'InvalidNewYorkBillingAgent':
    case 'ValidNewYorkBillingAgent':
      return isExhaustiveByType(options)
        ? options.NewYork(app)
        : options.NewYork
        ? options.NewYork(app)
        : options._(app);
    case 'InvalidOrdinaryBillingAgent':
    case 'ValidOrdinaryBillingAgent':
      return isExhaustiveByType(options)
        ? options.Ordinary(app)
        : options.Ordinary
        ? options.Ordinary(app)
        : options._(app);
  }
};

export const isValidBillingAgent = (billingAgent: BillingAgent) =>
  billingAgent.key === 'ValidGroupBillingAgent' ||
  billingAgent.key === 'ValidNewYorkBillingAgent' ||
  billingAgent.key === 'ValidOrdinaryBillingAgent';
